'use client';

import { useAuthUser } from '@/hooks/useAuthUser';
import { CacheManager } from '@/lib/cache';
import profileServices from '@/services/profile-services';
import { useCallback, useEffect, useState } from 'react';

interface UseAutoSavePreferenceResult {
  /** Whether auto-save of chat uploads to library is enabled. Defaults to true while loading. */
  autoSaveEnabled: boolean;
  /** Update the auto-save preference (optimistic update + API call). */
  setAutoSaveEnabled: (value: boolean) => Promise<void>;
  /** True until the initial fetch settles (or immediately for a logged-out user). Callers that gate visibility on `autoSaveEnabled` should wait for this to avoid acting on the loading default. */
  isLoading: boolean;
}

// Module-level cache + in-flight dedup, keyed by userId: this hook has
// multiple concurrent call sites (ExistingUserPrivacyNotice mounts in the
// root layout on every page; PreferencesPage and query-input's
// AutoSavePrompt gate call it too) — without this, a single page load can
// fire several identical GET /users/profile requests. Mirrors the dedup
// pattern in useUploadConfig.ts (CLAUDE.md: reuse CacheManager).
const preferenceCache = new CacheManager<boolean>(
  'AutoSavePreference',
  5 * 60 * 1000
);
const inFlightByUser = new Map<string, Promise<boolean>>();

function fetchPreference(userId: string): Promise<boolean> {
  const existing = inFlightByUser.get(userId);
  if (existing) return existing;

  const request = profileServices
    .getUserProfile()
    .then((response) => {
      // Server returns null for users who haven't set the preference yet
      // (column DEFAULT true, but may be null for pre-migration rows).
      const value = response.data?.auto_save_chat_uploads ?? true;
      preferenceCache.set(userId, value);
      return value;
    })
    .finally(() => {
      inFlightByUser.delete(userId);
    });

  inFlightByUser.set(userId, request);
  return request;
}

/**
 * Test-only escape hatch: the cache is module-level (shared across every
 * `useAutoSavePreference()` call site by design), so tests that assert on
 * distinct mocked responses must clear it between cases to avoid
 * cross-test leakage.
 */
export function __resetAutoSavePreferenceCacheForTests(): void {
  preferenceCache.clear();
  inFlightByUser.clear();
}

/**
 * Fetches and persists the auto_save_chat_uploads user preference.
 *
 * The preference is stored server-side in the users table so it survives
 * across browsers and devices. While loading, `autoSaveEnabled` defaults
 * to `true` to match the server default and avoid flash of "off" state.
 */
export function useAutoSavePreference(): UseAutoSavePreferenceResult {
  const { userId, isLoading: isAuthLoading } = useAuthUser();
  const cached = userId ? preferenceCache.get(userId) : null;
  const [autoSaveEnabled, setAutoSaveEnabledState] = useState<boolean>(
    cached ?? true
  );
  const [isLoading, setIsLoading] = useState<boolean>(cached === null);

  useEffect(() => {
    // Wait for auth itself to resolve before deciding anything. `userId`
    // is `undefined` both while auth is still being determined AND once
    // it resolves to "no user" — without this guard, a consumer gating on
    // `isLoading` (e.g. ExistingUserPrivacyNotice) would see this hook
    // report `isLoading: false` (the "genuinely logged out" default) for
    // one render on the very first auth resolution that DOES carry a
    // userId, because `setIsLoading(true)` below only takes effect on the
    // next render/commit — one commit too late to prevent the consumer's
    // own effect (same commit, later in hook order) from acting on the
    // still-default `autoSaveEnabled` value.
    if (isAuthLoading) return;

    if (!userId) {
      setIsLoading(false);
      return;
    }

    const fromCache = preferenceCache.get(userId);
    if (fromCache !== null) {
      setAutoSaveEnabledState(fromCache);
      setIsLoading(false);
      return;
    }

    // Re-entering a truthy userId (e.g. after a logged-out render) must
    // re-arm loading — otherwise a consumer gating on `isLoading` would see
    // it already `false` while `autoSaveEnabled` still holds the stale/
    // default value for the whole duration of this fetch.
    setIsLoading(true);

    fetchPreference(userId)
      .then((value) => {
        setAutoSaveEnabledState(value);
      })
      .catch((error) => {
        console.error('Failed to fetch auto-save preference:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userId, isAuthLoading]);

  const setAutoSaveEnabled = useCallback(
    async (value: boolean) => {
      // Optimistic update
      setAutoSaveEnabledState(value);
      if (userId) preferenceCache.set(userId, value);
      try {
        await profileServices.updateUserProfile({
          auto_save_chat_uploads: value,
        });
      } catch (error) {
        // Revert on failure
        setAutoSaveEnabledState(!value);
        if (userId) preferenceCache.set(userId, !value);
        console.error('Failed to update auto-save preference:', error);
      }
    },
    [userId]
  );

  return { autoSaveEnabled, setAutoSaveEnabled, isLoading };
}
